import React, { useEffect, useState } from 'react';
import classNames from 'classnames';
import { BringgFontIcons, BringgIcon } from '@bringg/bringg-icons';
import { Task, UserStatus } from '@bringg/types';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';
import moment from 'moment';
import { useAssignDriverSelectItem } from '../../../hooks/use-assign-driver-select-item';
import { Driver } from './assign-driver-select-item';
import useStores from '../../../recipes/use-stores';

interface Props {
	driver: Driver;
	task: Task;
}

// eslint-disable-next-line consistent-return
const renderDriverStatus = (driver: Driver, driverFreeTime: number, t: TFunction) => {
	if (driver.is_on_break) {
		return <span data-test-id="driverOnBreak"> [{t('DRIVER_ASSIGNMENT_DROP_DOWN.STATUS_BREAK')}]</span>;
	}

	if (driver.sub === UserStatus.ASSIGNED) {
		return <span data-test-id="driverIsAssigned"> [{t('UTILS.ORDER_STATUS_ASSIGNED')}]</span>;
	}

	if (driverFreeTime) {
		return <span data-test-id="driverFreeTime"> [{moment(driverFreeTime).fromNow()}]</span>;
	}

	if (driver.sub === UserStatus.FREE) {
		return <span data-test-id="driverIsFree"> [{t('DRIVER_ASSIGNMENT_DROP_DOWN.STATUS_FREE')}]</span>;
	}
};

const AssignDriverSelectLoggedInInThePast = ({ driver, task }: Props) => {
	const { driverFreeTime, driverLastAddress, vehicleTitleByType } = useAssignDriverSelectItem(driver);
	const { t } = useTranslation();
	const { taskInventoriesStore } = useStores();
	const [driverSub, setDriverSub] = useState((driver.sub ?? UserStatus.FREE).toLowerCase());

	useEffect(() => {
		if (driver.current_unavailability_id) {
			setDriverSub('driver-unavailable');
		} else if (driver.is_on_break) {
			setDriverSub(UserStatus.ON_BREAK.toLowerCase());
		}
	}, []);

	return (
		<span
			className={classNames(`sub_status_${driverSub}`, {
				'assign-driver-select-item-disabled': driver.disabled,
				status_offline: driver.status === 'offline'
			})}
		>
			{driver.disabled && <i className="bringg-icon bringg-icon-block" aria-hidden="true" />}
			<span className="assign-driver-select-item-text">
				<i className="circle" />
				<span data-test-id="driverName">{driver.name}</span>
			</span>
			{renderDriverStatus(driver, driverFreeTime, t)}
			{driverLastAddress && <span data-test-id="lastAddress"> ({driverLastAddress})</span>}
			{vehicleTitleByType && <span data-test-id="vehicleTitle">{` (${vehicleTitleByType})`}</span>}
			{!driver.active_shift_id && (
				<b data-test-id="driverOffShift"> [{t('DRIVER_ASSIGNMENT_DROP_DOWN.OFF_SHIFT')}]</b>
			)}
			{driver.active_shift_id && driver.current_unavailability_id && (
				<b data-test-id="driverUnavailable"> [{t('MAP_USER_PROFILE.DRIVER_UNAVAILABLE')}]</b>
			)}
			{taskInventoriesStore.userIdsWithAvailableInventoriesByTaskId[task.id]?.includes(driver.id) && (
				<BringgIcon
					iconName={BringgFontIcons.Package}
					data-test-id="hasAvailableInventory"
					className="assign-driver-select-item available_inventories_icon"
				/>
			)}
		</span>
	);
};

export default AssignDriverSelectLoggedInInThePast;
